import classNames from 'classnames';
import type { ReactFacebookLoginInfo } from 'react-facebook-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import type { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import GoogleLogin from 'react-google-login';
import { FormattedMessage } from 'react-intl';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import Typography from '@alltrails/core/components/Typography';
import Heading from '@alltrails/denali/components/Heading';
import Logo from '@alltrails/denali/components/Logo';
import Apple from '@alltrails/denali/icons/Apple';
import GoogleNoBackground from '@alltrails/denali/icons/GoogleNoBackground';
import { useModalAriaLabelledBy } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import altLoginsStyles from '../AltLoginsForm/AltLoginsForm.module.scss';
import FacebookButton from '../FacebookButton/FacebookButton';
import TermsOfService from '../TermsOfService';
import styles from './OAuthStep.module.scss';
import sharedStyles from './SignUpModal.module.scss';

type OAuthStepProps = {
  provider: 'Apple' | 'Google' | 'Facebook';
  appleLoginHandler: () => void;
  facebookLoginHandler: (response: ReactFacebookLoginInfo) => void;
  googleLoginHandler: (response: GoogleLoginResponse | GoogleLoginResponseOffline) => void;
};

const FACEBOOK_APP_ID = `${process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}` || '';

export default function OAuthStep({ provider, appleLoginHandler, facebookLoginHandler, googleLoginHandler }: OAuthStepProps) {
  const ariaLabelledBy = useModalAriaLabelledBy();
  const renderOAuthButton = () => {
    switch (provider) {
      case 'Google':
        return (
          <GoogleLogin
            clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}
            render={renderProps => (
              <button
                className={classNames(altLoginsStyles.google, altLoginsStyles.flatColors)}
                onClick={() => {
                  logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'google' });
                  renderProps.onClick();
                }}
                type="button"
              >
                <GoogleNoBackground className={altLoginsStyles.icon} />
                <span className={altLoginsStyles.text}>
                  <FormattedMessage defaultMessage="Continue with Google" />
                </span>
              </button>
            )}
            buttonText="Login"
            onSuccess={googleLoginHandler}
            cookiePolicy="single_host_origin"
          />
        );

      case 'Facebook':
        return (
          <FacebookLogin
            disableMobileRedirect
            appId={FACEBOOK_APP_ID}
            callback={(userInfo: ReactFacebookLoginInfo) => {
              if (!userInfo.accessToken) {
                return;
              }
              facebookLoginHandler(userInfo);
            }}
            scope="email,user_friends,user_gender,user_location"
            render={(renderProps: { onClick: () => void }) => (
              <FacebookButton
                onClick={() => {
                  logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'facebook' });
                  renderProps.onClick();
                }}
                flatColors
              >
                <FormattedMessage defaultMessage="Continue with Facebook" />
              </FacebookButton>
            )}
          />
        );

      case 'Apple':
        return (
          <button
            className={classNames(altLoginsStyles.apple, altLoginsStyles.flatColors)}
            onClick={() => {
              logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'apple' });
              appleLoginHandler();
            }}
            type="button"
          >
            <Apple className={altLoginsStyles.icon} color="--color-neutral-black" />
            <span className={altLoginsStyles.text}>
              <FormattedMessage defaultMessage="Continue with Apple" />
            </span>
          </button>
        );
    }
  };

  return (
    <div className={sharedStyles.content}>
      <div className={sharedStyles.logo}>
        <Logo variant="symbol" size="lg" color="monochrome-dark" />
      </div>
      <Heading as="h2" id={ariaLabelledBy} size="400">
        <FormattedMessage defaultMessage="Welcome back!" />
      </Heading>
      <Typography component="p" color="secondary" mb="0" mt="0" variant="paragraph200">
        <FormattedMessage defaultMessage="You previously created an account with {provider}" values={{ provider }} />
      </Typography>
      <div className={styles.oauthButton}>{renderOAuthButton()}</div>
      <TermsOfService />
    </div>
  );
}
